import React, { useContext, useState, useEffect } from 'react'
import { graphql } from 'gatsby'
import * as OverlayNavStyles from './OverlayNav.module.scss'
import { StaticQuery, Link, navigate } from 'gatsby'
import Social from '../../Social/Social'
import Hr from '../../Hr/Hr'
import Arrow from '../../../assets/arrow-left.svg'
import { COUNTIES } from '../../../lib/data/Sites'
import { type IOverlayMenuQueryData } from '../../../graphql/queries/OverlayMenuQuery'
import { fixAmpersands, LoginContext } from '../../../lib/utils'
import Search from '../../../assets/search.svg'

const OverlayMenuQuery = graphql`
  query {
    wpgraphql {
      overlayMenu: menu(id: "Overlay", idType: NAME) {
        menuItems(first: 100) {
          nodes {
            id
            path
            parentId
            label
            childItems {
              nodes {
                id
                path
                label
                childItems {
                  nodes {
                    id
                    label
                    path
                    childItems {
                      nodes {
                        id
                        label
                        path
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      secondaryMenu: menu(id: "Secondary", idType: NAME) {
        menuItems {
          nodes {
            id
            url
            label
          }
        }
      }
    }
  }
`

interface OverlayNavProps {
  visibility: boolean
  siteName?: string | null
  siteId?: number | null
  setSearchVisibility?: (visibility: boolean) => void
}

const OverlayNav = ({
  visibility,
  siteName,
  siteId,
  setSearchVisibility
}: OverlayNavProps) => {
  const [countyVisibility, setCountyVisibility] = useState<boolean>(false)
  const [logoutText, setLogoutText] = useState<string>('Log Out')
  const { usr: data, setUsr: setUser } = useContext(LoginContext)

  const handleLogout = () => {
    setLogoutText('Logging out...')
    try {
      localStorage.removeItem('token')
      setTimeout(() => {
        setUser && setUser({})
        setLogoutText('Log Out')
        const route =
          typeof window !== 'undefined' ? window.location.pathname : ''
        if (route === '/dashboard/') {
          navigate('/log-in/')
        }
      }, 1000)
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <StaticQuery
      query={OverlayMenuQuery}
      render={({
        wpgraphql: { overlayMenu, secondaryMenu }
      }: IOverlayMenuQueryData) => {
        const [schoolReviewLink, setSchoolReviewLink] =
          useState<JSX.Element | null>()

        useEffect(() => {
          // You can process your menu items here after they are fetched
          if (overlayMenu?.menuItems?.nodes) {
            overlayMenu.menuItems.nodes.forEach(item => {
              if (item?.label?.toLowerCase() === 'school reviews') {
                setSchoolReviewLink(
                  <li className={OverlayNavStyles.Title}>
                    <Link to={item?.path || ''}>
                      {item?.label?.replace('*', '')}
                    </Link>
                  </li>
                )
              }
            })
          }
        }, [overlayMenu?.menuItems?.nodes])
        // const siteUrl = MULTISITE_COUNTIES.find(c => c.id === siteId)?.url
        // const sanitizedSiteName = siteUrl?.split('.')[0].replace('https://', '')
        return (
          <div
            className={`${OverlayNavStyles.Wrapper} ${
              visibility ? OverlayNavStyles.IsOpen : ''
            }`}
          >
            <nav>
              <ul className={OverlayNavStyles.CountyList}>
                <li className={OverlayNavStyles.PrimaryLinks}>
                  {!data?.viewer ? (
                    <Link to={`/sign-up`}>Sign up</Link>
                  ) : (
                    <Link to={`/dashboard`}>Muddy Account</Link>
                  )}
                </li>
                <li className={OverlayNavStyles.PrimaryLinks}>
                  {data?.viewer ? (
                    <span
                      className={OverlayNavStyles.Link}
                      onClick={handleLogout}
                    >
                      {logoutText}
                    </span>
                  ) : (
                    <Link to={`/log-in`}>Log in</Link>
                  )}
                </li>
                {setSearchVisibility && (
                  <li className={OverlayNavStyles.PrimaryLinks}>
                    <span onClick={() => setSearchVisibility(true)}>
                      Search
                    </span>
                  </li>
                )}
                <li
                  className={`${OverlayNavStyles.Title} ${
                    countyVisibility ? OverlayNavStyles.IsOpen : ''
                  }`}
                  onClick={() => setCountyVisibility(!countyVisibility)}
                >
                  {siteId && siteId > 1
                    ? fixAmpersands(siteName ?? '')
                    : `Select county`}
                  <span className={OverlayNavStyles.Arrow}>
                    {<img src={Arrow} alt={`Arrow`} />}
                  </span>
                </li>
                {countyVisibility && (
                  <>
                    <li>
                      <a href="https://muddystilettos.co.uk">National</a>
                    </li>
                    {COUNTIES.map(({ siteId, name: title, url }) => (
                      <li key={siteId}>
                        <a href={url}>{title}</a>
                      </li>
                    ))}
                  </>
                )}
              </ul>
              <ul className={OverlayNavStyles.SecondaryNav}>
                {secondaryMenu?.menuItems?.nodes?.map(menuItem => (
                  <li className={OverlayNavStyles.Title} key={menuItem?.id}>
                    <a href={menuItem?.url || ''}>{menuItem?.label}</a>
                  </li>
                ))}
                <li className={OverlayNavStyles.Title}>
                  <a href="https://muddystilettos.co.uk/best-schools-awards/">
                    Best Schools Awards
                  </a>
                </li>
                <li className={OverlayNavStyles.Title}>
                  <Link
                    to={`https://muddystilettos.co.uk/house-that-muddy-built/`}
                  >
                    House That Muddy Built
                  </Link>
                </li>
              </ul>
              {overlayMenu?.menuItems?.nodes
                ?.filter(item => !item?.parentId)
                .filter(item => item?.label?.toLowerCase() !== 'school reviews')
                .map((item, i) => {
                  // const thisI = i as number
                  // const total: number =
                  //   overlayMenu?.menuItems?.nodes?.filter(
                  //     item => !item?.parentId
                  //   ).length ?? 0
                  // const lastIteration = (
                  //   <>
                  //     <li className={OverlayNavStyles.Title}>
                  //       <Link
                  //         to={`https://muddystilettos.co.uk/women-in-business/`}
                  //       >
                  //         She Means Business
                  //       </Link>
                  //     </li>
                  //   </>
                  // )

                  return (
                    <ul key={item?.id} className={OverlayNavStyles.FirstRow}>
                      <li className={OverlayNavStyles.Title}>
                        <Link to={item?.path || ''}>
                          {item?.label?.replace('*', '')}
                        </Link>
                      </li>
                      {item?.childItems?.nodes?.map(item => (
                        <li
                          key={item?.id}
                          className={`${
                            item?.childItems?.nodes &&
                            item?.childItems?.nodes?.length > 0
                              ? OverlayNavStyles.HasChildItems
                              : ''
                          }`}
                        >
                          <Link to={item?.path || ''}>
                            {item?.label &&
                              item?.label
                                .split(' ')
                                .map(
                                  (word: string) =>
                                    `${word.charAt(0).toUpperCase()}${
                                      word === 'UK' || word === 'TV'
                                        ? word.slice(1).toUpperCase()
                                        : word.slice(1).toLowerCase()
                                    } `
                                )}
                          </Link>
                          {item?.childItems?.nodes &&
                            item.childItems.nodes.length > 0 && (
                              <ul className={OverlayNavStyles.ChildNav}>
                                {item.childItems.nodes.map(item => (
                                  <li key={item?.id}>
                                    <Link to={item?.path || ''}>
                                      {item?.label &&
                                        item?.label
                                          .split(' ')
                                          .map(
                                            (word: string) =>
                                              `${word.charAt(0).toUpperCase()}${
                                                word === 'UK'
                                                  ? word.slice(1).toUpperCase()
                                                  : word.slice(1).toLowerCase()
                                              } `
                                          )}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                        </li>
                      ))}
                      {/* {thisI === total - 1 && lastIteration} */}
                    </ul>
                  )
                })}
              <ul>
                {schoolReviewLink}
                <li className={OverlayNavStyles.Title}>
                  <Link to={`https://muddystilettos.co.uk/school-reviews/`}>
                    Best Schools Guide
                  </Link>
                </li>
                <li className={OverlayNavStyles.Title}>
                  <Link
                    to={`https://muddystilettos.co.uk/house-that-muddy-built/`}
                  >
                    House That Muddy Built
                  </Link>
                </li>
              </ul>
              <ul>
                <li className={OverlayNavStyles.Title}>
                  <Link to={`/about`}>About Us</Link>
                </li>
                <li className={OverlayNavStyles.Title}>
                  <a href={`https://muddystilettos.co.uk/work-with-us`}>
                    Work With Us
                  </a>
                </li>
                <li className={OverlayNavStyles.Title}>
                  <a href={`https://muddystilettos.co.uk/contact`}>Contact</a>
                </li>
              </ul>
              <ul>
                <li className={OverlayNavStyles.Title}>Follow Us</li>
                <Social asText />
              </ul>
              <Hr />
            </nav>
          </div>
        )
      }}
    />
  )
}

export default OverlayNav
